import { html } from '@crossiety/lit-components';
import BaseMenu from '@crossiety/lit-components/components/base_menu/element.js';
import '@crossiety/lit-components/components/dot_menu/element.js';

import translations from './translations.js';

/**
 * Dot dot menu for feed import item
 *
 * @prop editOn - Should show edit option?
 * @prop updateOn - Should show update option?
 * @prop deleteOn - Should show delete option?
 * @prop groupId - Id of the group
 * @prop feedId - Id of the feed
 * @prop feedType - The type of feed (options: event_frog, rss, iweb)
 */

export default class FeedMenu extends BaseMenu {
  static properties = {
    editOn: { type: Boolean },
    updateOn: { type: Boolean },
    deleteOn: { type: Boolean },
    groupId: { type: Number },
    feedId: { type: Number },
    feedType: { type: String },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get instanceId() {
    return `${this.groupId}-${this.feedId}`;
  }

  /** @internal */
  get baseUrl() {
    return `/groups/${this.groupId}/${this.feedType}_sources/${this.feedId}`;
  }

  /** @internal */
  get editUrl() {
    return `${this.baseUrl}/edit`;
  }

  /** @internal */
  get updateUrl() {
    return `/groups/${this.groupId}/group_import_sources/${this.feedId}/import_feed`;
  }

  /** @internal */
  get editItem() {
    return {
      title: this.t('common_actions.edit'),
      action: this.actionFor('feedExecJSFromResponse', [this.editUrl]),
      icon: 'pencil',
      partName: 'edit',
    };
  }

  /** @internal */
  get updateItem() {
    return {
      title: this.t('actions.update'),
      action: this.actionFor('feedUpdate'),
      icon: 'download',
      partName: 'update',
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('common_actions.delete'),
      action: this.actionFor('feedDelete'),
      icon: 'trash-can',
      partName: 'delete destructive',
      role: 'destructive',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get items() {
    return [
      this.updateOn && this.updateItem,
      this.editOn && this.editItem,
      this.deleteOn && (this.updateOn || this.editOn) && this.divider,
      this.deleteOn && this.deleteItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      id: this.instanceId,
      menu: 'xs-feed-menu',
      method,
      args,
    };
  }

  feedExecJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  async feedUpdate() {
    try {
      await this.commonActions.post(this.updateUrl);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  triggerFeedDeletedEvent() {
    this.dispatchEvent(
      new Event('feed-deleted', { bubbles: true, composed: true })
    );
  }

  async feedDelete() {
    if (!this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default')))
      return false;

    try {
      await this.commonActions.delete(this.baseUrl);
      this.triggerFeedDeletedEvent();
      this.commonActions.displaySnackBarSuccess(
        this.t('snack.feed_delete_success')
      );
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  render() {
    return html`<ef-dot-menu .config=${this.dotMenuConfig}></ef-dot-menu>`;
  }
}

FeedMenu.registerTagName('xs-feed-menu');
FeedMenu.registerCommonActions('xs-feed-menu');

customElements.get('xs-feed-menu') ||
  customElements.define('xs-feed-menu', FeedMenu);
